import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataGrid from '../../../../table/DataGrid';
import { getModule } from '../../../../../services/maintenance/maintenance.service';
import {
  Alert,
  Box,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Skeleton,
  Typography
} from '@mui/material';
import groupBy from 'lodash/groupBy';
import { handlerErrorV2 } from '../../../../../utils/errors';
import { compareListClasses, compareListUsers } from './utils';

const ListCompare = ({ module, userType, toggleLoadingList, loadingList, setError, updateYearLevels, updateSubject }) => {
  const [list, setList] = useState([]);
  const [messages, setMessages] = useState({});

  const onLoad = useCallback(
    async (userType, updateYearLevels, updateSubject) => {
      toggleLoadingList(true);
      try {
        const response = await getModule(module, { userType });
        if (!response.data && response.error) {
          setError(response.error.message || 'An error ocurred');
        } else {
          const data = response.data;

          if (module === 'subjects') {
            const list = data.map((item) => {
              let color = 'default';
              if (item.action) {
                if (item.action === 'update') {
                  color = 'info';
                }
                if (item.action === 'disable') {
                  color = item.error ? 'error' : 'warning';
                }
                if (item.action === 'create') {
                  color = 'success';
                }
              }
              return {
              ...item,
              _id: item.code,
              name: item.name || item?.current?.name,
              status: <Chip component={'div'} label={item.message} size='small' color={color} />,
              changes: item.changes ? (
                <code>
                  <pre className='bg-muted-foreground text-muted p-2'>{JSON.stringify(item.changes, null, 4)}</pre>
                </code>
              ) : (
                ''
              )
            }
            });
            setList(list);
            setMessages(groupBy(list, 'message'));
            toggleLoadingList(false);
            return;
          }

          const currentList = data.current.data;
          const syncList = data.sync.data.map((item) => {
            let message = '';
            let color = 'default';
            let error = false;
            let changes = {};
            if (module === 'users') {
              const compare = compareListUsers(currentList, item, data.sync.data);
              message = compare.message;
              error = compare.error;
              color = compare.color;
              changes = compare.changes;
            }
            if (module === 'classes') {
              const compare = compareListClasses(currentList, item, updateYearLevels, updateSubject);
              message = compare.message;
              error = compare.error;
              color = compare.color;
            }

            return {
              ...item,
              _id: item.code + item.email,
              students: item.studentsCode ? item.studentsCode.join(', ') : '',
              teachers: item.teachersCode ? item.teachersCode.join(', ') : '',
              yearLevel: Array.isArray(item.yearLevelName) ? item.yearLevelName.join(', ') : item.yearLevel,
              children: item.children ? item.children.join(', ') : '',
              status: <Chip component={'div'} label={message} size='small' color={color} />,
              message,
              color,
              changes: changes ? (
                <code>
                  <pre className='bg-muted-foreground text-muted p-2'>{JSON.stringify(changes, null, 4)}</pre>
                </code>
              ) : (
                ''
              )
            };
          });
          setList(syncList);
          setMessages(groupBy(syncList, 'message'));
        }
      } catch (error) {
        const message = handlerErrorV2(error);
        setError(message);
      } finally {
        toggleLoadingList(false);
      }
    },
    [module]
  );

  useEffect(() => {
    if (module) {
      onLoad(userType, updateYearLevels, updateSubject);
    }
  }, [userType, updateYearLevels, updateSubject, module, onLoad]);

  const columnsTable = useMemo(() => {
    let cols = [{ label: 'code', key: 'code', width: 50, show: true, hide: false }];

    if (module === 'users') {
      cols = [
        { label: 'code', key: 'code', width: 50, show: true },
        {
          label: 'First Name',
          key: 'firstName',
          show: true
        },
        {
          label: 'Last Name',
          key: 'lastName',
          show: true
        },
        {
          label: 'Email',
          key: 'email',
          show: true
        }
      ];

      if (userType === 'Students') {
        cols = [
          ...cols,
          {
            label: 'Graduation Year',
            hide: true,
            key: 'graduationYear',
            show: true,
            delete: true,
            filter: 'text'
          },
          {
            label: 'Year level',
            hide: true,
            key: 'yearLevel',
            show: true,
            delete: true,
            filter: 'text'
          }
        ];
      }
      if (userType === 'Teachers') {
        cols = [
          { label: 'code', key: 'code', width: 50, show: true },
          {
            label: 'title',
            key: 'title',
            show: true
          },
          {
            label: 'First Name',
            key: 'firstName',
            show: true
          },
          {
            label: 'Last Name',
            key: 'lastName',
            show: true
          },
          {
            label: 'Email',
            key: 'email',
            show: true
          }
        ];
      }
      if (userType === 'Parents') {
        cols = [
          { label: 'code', key: 'code', width: 50, show: true },
          {
            label: 'First Name',
            key: 'firstName',
            show: true
          },
          {
            label: 'Email',
            key: 'email',
            show: true
          },
          {
            label: 'children',
            hide: true,
            key: 'children',
            show: true,
            delete: true,
            filter: 'text'
          }
        ];
      }
    }
    if (module === 'classes') {
      cols = [
        ...cols,
        {
          label: 'Year level',
          hide: true,
          key: 'yearLevel',
          show: true,
          delete: true,
          filter: 'text'
        },
        {
          label: 'Students',
          hide: true,
          key: 'students',
          show: true,
          delete: true,
          filter: 'text'
        },
        {
          label: 'Teachers',
          hide: true,
          key: 'teachers',
          show: true,
          delete: true,
          filter: 'text'
        }
      ];
    }

    if (module === 'subjects') {
      cols = [
        ...cols,
        {
          label: 'Name',
          key: 'name',
          show: true
        }
      ];
    }

    return cols;
  }, [module, userType]);

  return (
    <>
      <Box
        className={[
          'bg-primary text-primary-foreground',
          'px-4 py-1 flex justify-between items-center gap-1 h-8'
        ].join(' ')}
      >
        <Typography variant='subtitle1' className='uppercase'>
          {module === 'users' ? userType : ''} {module}
        </Typography>
        <Typography className='bg-primary-foreground text-primary rounded-lg text-sm py-1 px-2'>
          {loadingList ? <Skeleton width={60} /> : `${list?.length || 0} ${module}`}
        </Typography>
      </Box>
      <div className='flex flex-col gap-1 relative'>
        <TableContainer>
          <Table>
            <TableBody>
              {loadingList
                ? [1, 2, 3].map((index) => (
                    <TableRow key={`loading-${index}`}>
                      <TableCell width={600}>
                        <Skeleton />
                      </TableCell>
                      <TableCell align='right'>
                        <Skeleton />
                      </TableCell>
                    </TableRow>
                  ))
                : Object.keys(messages).map((key, index) => (
                    <TableRow key={`${module}-${userType}-${index}`}>
                      <TableCell width={600}>{messages[key]?.[0]?.status}</TableCell>
                      <TableCell align='right'>
                        {messages[key]?.length} {module}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <div className='h-screen-1/2 relative'>
        <DataGrid
          id={`${module}-list`}
          groupBy={'message'}
          rows={list.map((item, index) => ({ ...item, index: index + 1 }))}
          loading={loadingList}
          totalRows={list.length || 10}
          rowsPerPage={list.length || 10}
          showSetting={false}
          columns={[
            { label: '#', key: 'index', width: 10, show: true, hide: false },
            ...columnsTable,
            { label: 'status', key: 'status', align: 'center', width: 100, show: true, hide: false },
            { label: 'changes', key: 'changes', width: 100, show: false, hide: false }
          ]}
          labelNoRows={`You don't have any ${module} yet`}
        />
      </div>
    </>
  );
};

export default ListCompare;
